// Small animation helpers shared by the pool screens.
// Everything respects the OS "reduce motion" setting.
import React, { useEffect, useMemo, useRef, useState } from 'react';

export const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Animates a number from its previous value to `value` (ease-out).
export function useCountUp(value, duration = 900) {
  const target = Number(value) || 0;
  const [shown, setShown] = useState(prefersReducedMotion() ? target : 0);
  const shownRef = useRef(shown); // where the number currently is on screen

  useEffect(() => {
    if (prefersReducedMotion()) { setShown(target); shownRef.current = target; return undefined; }
    const from = shownRef.current;
    if (from === target) return undefined;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      const v = from + (target - from) * eased;
      shownRef.current = v;
      setShown(v);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    // Interrupted (new value / unmount / StrictMode re-run): the next run continues from shownRef
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);

  return shown;
}

// <CountUp value={1234} format={(n) => formatMoney(n, lang)} />
export function CountUp({ value, format = (n) => Math.round(n), duration }) {
  const n = useCountUp(value, duration);
  return <>{format(n)}</>;
}

// One-shot confetti burst. Pure CSS pieces; unmounts itself.
const CONFETTI_COLORS = ['var(--accent)', '#fbbf24', '#22c55e', '#3b82f6', '#ffffff', 'var(--header-accent)'];
export function Confetti({ pieces = 70, durationMs = 3200 }) {
  const [alive, setAlive] = useState(!prefersReducedMotion());
  useEffect(() => {
    if (!alive) return undefined;
    const id = setTimeout(() => setAlive(false), durationMs + 400);
    return () => clearTimeout(id);
  }, [alive, durationMs]);

  const bits = useMemo(() => Array.from({ length: pieces }, (_, i) => ({
    left: Math.random() * 100,
    delay: Math.random() * 600,
    dur: durationMs * (0.6 + Math.random() * 0.4),
    drift: (Math.random() - 0.5) * 240,
    spin: (Math.random() - 0.5) * 1440,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    w: 6 + Math.random() * 6,
    h: 8 + Math.random() * 10,
  })), [pieces, durationMs]);

  if (!alive) return null;
  return (
    <div className="pfx-confetti" aria-hidden="true">
      {bits.map((b, i) => (
        <span
          key={i}
          style={{
            left: `${b.left}%`,
            width: b.w,
            height: b.h,
            background: b.color,
            animationDelay: `${b.delay}ms`,
            animationDuration: `${b.dur}ms`,
            '--drift': `${b.drift}px`,
            '--spin': `${b.spin}deg`,
          }}
        />
      ))}
    </div>
  );
}

// Seconds left until an ISO deadline, ticking every second.
export function useSecondsLeft(deadlineIso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!deadlineIso) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [deadlineIso]);
  if (!deadlineIso) return null;
  return Math.max(0, Math.ceil((new Date(deadlineIso).getTime() - now) / 1000));
}

export const fmtClock = (s) => (s == null ? '' : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`);

// Which draft slot is on the clock for a pick number.
//   teamRound: picks 1..n = team round in REVERSE order (n → 1), then players snake starting at slot 1.
//   otherwise: classic snake from pick 1. (Must match pool_slot_on_clock in the SQL.)
export function slotOnClock(pickNumber, n, teamRound = false) {
  if (!n || pickNumber < 1) return null;
  let k = pickNumber;
  if (teamRound) {
    if (pickNumber <= n) return n - pickNumber + 1;
    k = pickNumber - n;
  }
  const roundIdx = Math.floor((k - 1) / n);
  const pos = (k - 1) % n;
  return roundIdx % 2 === 0 ? pos + 1 : n - pos;
}

// ── "Your team": the NHL team a member drafted becomes their identity in the pool ──
export const NHL_COLORS = {
  ANA: ['#F47A38', '#B9975B'], BOS: ['#FFB81C', '#111111'], BUF: ['#002654', '#FCB514'], CGY: ['#D2001C', '#FAAF19'],
  CAR: ['#CC0000', '#111111'], CHI: ['#CF0A2C', '#111111'], COL: ['#6F263D', '#236192'], CBJ: ['#002654', '#CE1141'],
  DAL: ['#006847', '#8F8F8C'], DET: ['#CE1126', '#111111'], EDM: ['#041E42', '#FF4C00'], FLA: ['#041E42', '#C8102E'],
  LAK: ['#111111', '#A2AAAD'], MIN: ['#154734', '#A6192E'], MTL: ['#AF1E2D', '#192168'], NSH: ['#FFB81C', '#041E42'],
  NJD: ['#CE1126', '#111111'], NYI: ['#00539B', '#F47D30'], NYR: ['#0038A8', '#CE1126'], OTT: ['#C52032', '#C2912C'],
  PHI: ['#F74902', '#111111'], PIT: ['#111111', '#FCB514'], SJS: ['#006D75', '#EA7200'], SEA: ['#001628', '#99D9D9'],
  STL: ['#002F87', '#FCB514'], TBL: ['#002868', '#9CA3AF'], TOR: ['#00205B', '#9CA3AF'], UTA: ['#71AFE5', '#090909'],
  VAN: ['#00205B', '#00843D'], VGK: ['#B4975A', '#333F42'], WSH: ['#C8102E', '#041E42'], WPG: ['#041E42', '#004C97'],
};
export const teamLogo = (abbrev) => (abbrev ? `https://assets.nhle.com/logos/nhl/svg/${abbrev}_light.svg` : null);

// user_id -> { abbrev, name, short } from the picks (position "T" = drafted team)
export function teamsByUser(picks) {
  const map = {};
  (picks || []).forEach((p) => {
    if (p.position !== 'T') return;
    const name = p.player_name || p.team;
    const words = String(name).split(' ');
    // short name: "Montréal Canadiens" -> "Canadiens", "Toronto Maple Leafs" -> "Maple Leafs"
    const short = /Maple Leafs|Red Wings|Blue Jackets|Golden Knights/.test(name) ? words.slice(-2).join(' ') : words.slice(-1)[0];
    map[p.user_id] = { abbrev: p.team, name, short };
  });
  return map;
}

// Small avatar: the member's team logo if they have one (team.logo = any league's logo, else NHL), else their initials.
export function TeamAvatar({ team, name, size = 28, className = '' }) {
  if (team?.abbrev) {
    return (
      <span className={`pfx-avatar logo ${className}`} style={{ width: size, height: size }} title={team.name}>
        <img src={team.logo || teamLogo(team.abbrev)} alt={team.name} onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
      </span>
    );
  }
  return (
    <span className={`pfx-avatar ${className}`} style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}>
      {(name || '—').slice(0, 2).toUpperCase()}
    </span>
  );
}

// Laptops: scale a whole screen as one piece (same proportions as full size) when its
// container is narrower than the design width. Below 900px (tablet/phone) it turns off
// and the responsive layout takes over. Returns a ref to put on the screen's root.
export function useFitWidth(designWidth, deps = []) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    const parent = el?.parentElement;
    if (!el || !parent) return undefined;
    let raf = 0;
    const apply = () => {
      raf = 0;
      const cs = window.getComputedStyle(parent);
      const avail = parent.clientWidth - parseFloat(cs.paddingLeft || 0) - parseFloat(cs.paddingRight || 0);
      const scaled = window.innerWidth > 900 && avail < designWidth;
      const zoom = scaled ? String(Math.max(0.6, Math.round((avail / designWidth) * 1000) / 1000)) : '';
      // only touch the page when something really changed (avoids resize loops)
      if (el.style.zoom === zoom) return;
      el.style.zoom = zoom;
      el.style.width = scaled ? `${designWidth}px` : '';
      el.style.maxWidth = scaled ? 'none' : '';
    };
    // apply on the next frame, never inside the ResizeObserver callback itself
    const fit = () => { if (!raf) raf = requestAnimationFrame(apply); };
    apply();
    const ro = new ResizeObserver(fit);
    ro.observe(parent);
    window.addEventListener('resize', fit);
    return () => { if (raf) cancelAnimationFrame(raf); ro.disconnect(); window.removeEventListener('resize', fit); };
  }, [designWidth, ...deps]);
  return ref;
}

// ── Lineup mode formats (must match lineup_slot_capacity / lineup_slot_multiplier in SQL) ──
// Each slot: id, position, how many players, scoring multiplier.
const S = (id, pos, cap, mult) => ({ id, pos, cap, mult });
export const LINEUP_FORMATS = {
  small:  [S('F1', 'F', 3, 2),                                                        S('FX', 'F', 1, 0),
           S('D1', 'D', 2, 2),                                                        S('DX', 'D', 1, 0),
           S('G1', 'G', 1, 1)],
  medium: [S('F1', 'F', 3, 2), S('F2', 'F', 3, 1),                                    S('FX', 'F', 1, 0),
           S('D1', 'D', 2, 2),                                                        S('DX', 'D', 1, 0),
           S('G1', 'G', 1, 1)],
  large:  [S('F1', 'F', 3, 2), S('F2', 'F', 3, 1), S('F3', 'F', 3, 1),                S('FX', 'F', 1, 0),
           S('D1', 'D', 2, 2), S('D2', 'D', 2, 1),                                    S('DX', 'D', 1, 0),
           S('G1', 'G', 1, 1)],
  full:   [S('F1', 'F', 3, 2), S('F2', 'F', 3, 1), S('F3', 'F', 3, 1), S('F4', 'F', 3, 1), S('FX', 'F', 2, 0),
           S('D1', 'D', 2, 2), S('D2', 'D', 2, 1), S('D3', 'D', 2, 1),                S('DX', 'D', 1, 0),
           S('G1', 'G', 1, 1), S('GX', 'G', 1, 0)],
};
export const lineupTotals = (format) => (LINEUP_FORMATS[format] || []).reduce((t, s) => {
  t[s.pos] = (t[s.pos] || 0) + s.cap; return t;
}, { F: 0, D: 0, G: 0 });
export const slotLabel = (slotId, fr = false) => {
  if (!slotId) return '';
  const n = slotId.slice(1);
  if (n === 'X') return fr ? 'Réserve' : 'Extra';
  if (slotId[0] === 'F') return `${fr ? 'Trio' : 'Line'} ${n}`;
  if (slotId[0] === 'D') return `${fr ? 'Paire' : 'Pair'} ${n}`;
  return fr ? 'Partant' : 'Starter';
};

// ── Salary cap (real 2026-27 cap hits; matches supabase-migrations/2026-09-29_salary_cap.sql) ──
export const NHL_CAP = 104000000;
// Drafting / list making uses LAST season's pool points (this season's are only a few games old).
// Falls back to this season's points until the server has last season loaded.
export const draftPts = (p) => (p?.lastPoints ?? p?.points ?? 0);
export const LAST_SEASON = (() => { const d = new Date(); const y = d.getMonth() >= 8 ? d.getFullYear() : d.getFullYear() - 1; return `${String(y - 1).slice(2)}-${String(y).slice(2)}`; })();

export const NHL_CAP_MIN = 850000;       // league minimum = cap hit of anyone without a contract
// real cap scaled to a pool roster (players only — the NHL team isn't paid), rounded to $100K
export const defaultCap = (players) => Math.round((NHL_CAP * Math.max(1, players)) / 23 / 100000) * 100000;
export const fmtCap = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1000000) return `$${(v / 1000000).toFixed(2).replace(/\.?0+$/, '')}M`;   // $12.6M · $11.57M · $50M
  return `$${Math.round(v / 1000)}K`;
};
export const payrollOf = (picks) => (picks || []).reduce((sum, p) => sum + (p.position === 'T' ? 0 : Number(p.cap_hit ?? p.capHit ?? 0)), 0);
// hard cap, during the draft: payroll + his cap hit + league minimum for every spot still to fill ≤ cap
export function capFits(pool, myPlayerPicks, playersPerTeam, player) {
  if (pool?.cap_mode !== 'hard' || !player || player.position === 'T') return true;
  const leftAfter = Math.max(0, playersPerTeam - myPlayerPicks.length - 1);
  return payrollOf(myPlayerPicks) + (player.capHit ?? NHL_CAP_MIN) + leftAfter * NHL_CAP_MIN <= pool.cap_amount;
}

// Payroll meter: "PAYROLL ▬▬▬▬▬░░ $31.2M / $49.7M" (red when over)
export function CapMeter({ payroll, cap, label = 'Payroll', className = '' }) {
  const over = payroll > cap;
  const pct = cap > 0 ? Math.min(100, (payroll / cap) * 100) : 0;
  return (
    <div className={`cap-meter ${over ? 'over' : ''} ${className}`}>
      <span className="cap-meter-label">{label}</span>
      <span className="cap-meter-bar"><i style={{ width: `${pct}%` }} /></span>
      <span className="cap-meter-num">{fmtCap(payroll)} <small>/ {fmtCap(cap)}</small></span>
    </div>
  );
}

// A player's stable key: his NHL id, whatever team prefix his pool id carries
// ("ana-8474590" and "tbl-8474590" are the same player). Teams keep "team-xxx".
export const pkey = (id) => (String(id).startsWith('team-') ? String(id) : String(id).split('-').pop());
